import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "./AuthContext";
import { authApi } from "../api/auth";
import { totpApi, type TotpSetup } from "../api/totp";
import type { ApiError } from "../api/client";
import { Loading } from "../components/Loading";

/**
 * Full-screen, blocking 2FA enrolment shown when the current user's grace window has elapsed and
 * they still have no authenticator (server sets twoFactor.setupRequired + a totp_pending token that
 * only reaches the enrolment endpoints). There is no "skip": the only ways out are enrolling or
 * signing out. On success we re-hydrate the session (refresh()), which clears setupRequired and
 * drops the user into the app.
 */
export default function MandatoryTwoFactorSetup() {
  const { t } = useTranslation();
  const { logout, refresh, state } = useAuth();
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const email = state.status === "authenticated" ? state.user.email : "";

  // Provision a secret up front so the QR/secret is ready as soon as the screen appears.
  useEffect(() => {
    let cancelled = false;
    void totpApi
      .setup()
      .then((s) => { if (!cancelled) setSetup(s); })
      .catch((err) => { if (!cancelled) setError((err as ApiError).message); });
    return () => { cancelled = true; };
  }, []);

  async function confirm(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const res = await totpApi.confirm(code);
      // The access token in the cookie still carries totp_pending=true (claims are baked at issue
      // time). Rotate it now so the fresh token reflects the enrolment and the server-side gate lifts
      // — otherwise business calls would 403 until the old token expired.
      await authApi.refresh();
      setRecoveryCodes(res.recoveryCodes);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={shell}>
      <div style={card}>
        <img src="/assets/logo.jpg" alt="IMP Pumps" style={logo} />
        <h1 style={title}>{t("twofa.required.title", "Two-factor authentication required")}</h1>
        <p style={subtitle}>
          {t(
            "twofa.required.intro",
            "Your role requires two-factor authentication. Set it up now to continue to the portal.",
          )}
        </p>

        {error && <div style={errorBox}>{error}</div>}

        {/* Enrolment succeeded: show recovery codes once, then let them enter the app. */}
        {recoveryCodes ? (
          <>
            <div style={recoveryBox}>
              <strong style={{ color: "#92400e", fontSize: 13 }}>
                {t("twofa.required.recoveryTitle", "Save your recovery codes")}
              </strong>
              <p style={{ margin: "4px 0 8px", fontSize: 12, color: "#92400e" }}>
                {t(
                  "twofa.required.recoveryHint",
                  "Store these somewhere safe. Each can be used once if you lose access to your authenticator. They will not be shown again.",
                )}
              </p>
              <ul style={recoveryList}>
                {recoveryCodes.map((c) => (
                  <li key={c} style={recoveryItem}>{c}</li>
                ))}
              </ul>
            </div>
            <button type="button" onClick={() => void refresh()} style={primaryButton}>
              {t("twofa.required.continue", "Continue to portal")}
            </button>
          </>
        ) : (
          <form onSubmit={confirm} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: 14, color: "#1f2937" }}>
              {t(
                "twofa.required.scanHint",
                "Scan this QR (or paste the secret) into an authenticator app like Google Authenticator, 1Password or Authy, then enter the 6-digit code it shows.",
              )}
            </p>
            {setup ? (
              <>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <QRCodeSVG value={setup.provisioningUri} size={184} marginSize={2} level="M" />
                </div>
                <p style={{ margin: 0, fontSize: 12, color: "#6b7280", wordBreak: "break-all" }}>
                  {t("twofa.required.enterManually", "Or enter this secret manually:")}{" "}
                  <strong>{setup.secretBase32}</strong>
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  style={codeInput}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <button type="submit" style={primaryButton} disabled={busy || code.length !== 6}>
                  {busy ? "…" : t("twofa.required.confirm", "Confirm & continue")}
                </button>
              </>
            ) : (
              !error && <p style={{ fontSize: 13, color: "#6b7280" }}><Loading label={t("common.loading", "Loading…")} /></p>
            )}
          </form>
        )}

        <button type="button" onClick={() => void logout()} style={signOutLink}>
          {t("twofa.required.signOut", "Sign out")}
          {email ? ` (${email})` : ""}
        </button>
      </div>
    </div>
  );
}

const shell: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f4f6f8",
  padding: 16,
};
const card: React.CSSProperties = {
  width: "100%",
  maxWidth: 440,
  background: "#fff",
  padding: 32,
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const logo: React.CSSProperties = { width: 96, height: "auto", alignSelf: "center", marginBottom: 4 };
const title: React.CSSProperties = { margin: 0, color: "#0b3a6f", fontSize: 22 };
const subtitle: React.CSSProperties = { margin: 0, color: "#6b7280", fontSize: 14 };
const codeInput: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  fontSize: 18,
  letterSpacing: 6,
  textAlign: "center",
  maxWidth: 220,
  alignSelf: "center",
};
const primaryButton: React.CSSProperties = {
  padding: "12px 16px",
  background: "#0b3a6f",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
const signOutLink: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "#6b7280",
  fontSize: 13,
  cursor: "pointer",
  textAlign: "center",
  textDecoration: "underline",
};
const errorBox: React.CSSProperties = {
  padding: 12,
  background: "#fef2f2",
  color: "#b91c1c",
  borderRadius: 6,
  fontSize: 14,
};
const recoveryBox: React.CSSProperties = {
  background: "#fffbeb",
  border: "1px solid #fde68a",
  borderRadius: 8,
  padding: 12,
};
const recoveryList: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 4,
};
const recoveryItem: React.CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 13,
  color: "#1f2937",
};
